import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  Card,
  Divider,
  FormField,
  FormHelp,
  Heading,
  Image,
  Input,
  Label,
  Paragraph,
  TextButton,
  ToneIcon,
  Tooltip,
} from '@zillow/constellation-10';
import { IconLockClosedOutline } from '@zillow/constellation-icons';
import { logError } from '@zg-rentals/log-error';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';
import { useUpdateLandlordPaymentMethodWebApiHandler_updateLandlordPaymentMethod } from '@zg-rentals/ts-rent-payment-client-react-query';

import DebouncedButton from '@common/components/ConstellationDebouncedButton';
import PlaidButton from '@common/components/PlaidButton';
import FaqComponent from '@common/components/ConstellationFaqComponent';
import Modal from '@common/components/ConstellationV10Modal';
import FaqConstants from '@rental-manager/constants/ConstellationFaqConstants';
import CheckImageToolTip from '@assets/check-image-tooltip.png';
import userPersonas from '@common/constants/userPersonas';
import { DEFAULT_ERROR, INVALID_ACCOUNT_INFO } from '@rental-manager/constants/NotificationConstants';
import { validateBankInfo } from './validation';
import type { IConfigModel } from '@rental-manager/models/Types';
import './index.scss';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import { closeNotification } from '@common/actions/NotificationActions';
import { saveAccountInfo } from '@rental-manager/actions/OnboardingActions';

declare const window: {
  Stripe: any;
};

interface Props {
  config: IConfigModel;
  fromDashboard: boolean;
  onBoarded: boolean;
  accountInfo: {
    editMode: boolean;
    paymentType: string;
    routingNumber: string;
    checkingAccountNumber: string;
  };
  onClose: () => void;
  onCancel: () => void;
  onUpdatePaymentMethodSuccess: () => void;
  onBack: ({ paymentType, routingNumber, checkingAccountNumber }) => void;
  trackValidationError: ({ plaidErrorsLastEncountered, plaidMetadata }) => void;
  showModalNotification: (args: string) => Record<string, any>;
  getPlaidLinkToken: () => any;
  selectedTaxVerificationType: string;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  onClickForStorybook?: () => void;
  propertyId: string;
}

interface TokenResponse {
  token: string;
  error: string;
}

type PaymentMethodToken = {
  plaidToken?: Record<string, any>;
  stripeExternalAccountToken?: string;
};

type UpdatePaymentPackage = {
  accountInfo?: {
    paymentType: string;
    plaidBankName?: string;
    routingNumber?: string;
    checkingAccountNumber?: string;
  };
  token: PaymentMethodToken;
};

const AccountInfoPage: React.FC<Props> = ({
  config,
  fromDashboard = false,
  onBoarded = false,
  accountInfo = {
    paymentType: 'plaid',
    routingNumber: '',
    checkingAccountNumber: '',
    editMode: false,
  },
  onClose,
  onCancel,
  onUpdatePaymentMethodSuccess,
  onBack,
  trackValidationError,
  showModalNotification,
  propertyId,
  getPlaidLinkToken,
  selectedTaxVerificationType,
  currentProperty,
  user,
  onClickForStorybook,
}) => {
  const dispatch = useDispatch();
  const [stripe, setStripe] = useState<any>(null);
  const [paymentType, setPaymentType] = useState<string>(accountInfo.paymentType);
  const [routingNumber, setRoutingNumber] = useState({
    value: accountInfo.routingNumber,
    error: '',
  });
  const [checkingAccountNumber, setCheckingAccountNumber] = useState({
    value: accountInfo.checkingAccountNumber,
    error: '',
  });
  const [confirmAccountNumber, setConfirmAccountNumber] = useState({
    value: '',
    error: '',
  });
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { mutateAsync: updateLandlordPaymentMethod } =
    useUpdateLandlordPaymentMethodWebApiHandler_updateLandlordPaymentMethod();

  // Ref for package data
  const pkgRef = useRef<UpdatePaymentPackage>({
    accountInfo: {
      paymentType: '',
      plaidBankName: '',
      routingNumber: '',
      checkingAccountNumber: '',
    },
    token: {
      plaidToken: {},
    },
  });

  // Effect hook to replace componentDidMount
  useEffect(() => {
    // https://github.com/stripe/react-stripe-elements#loading-stripejs-asynchronously
    // note: useEffect doesn't run on the server
    if (window.Stripe) {
      setStripe(window.Stripe(config.stripePublicKey));
    } else {
      const el = document.querySelector('#stripe-js');
      el?.addEventListener('load', () => {
        setStripe(window.Stripe(config.stripePublicKey));
      });
    }
  }, [config.stripePublicKey]);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const key = e.target.dataset.statekey;
    const value = e.target.value;

    const setterMap = {
      routingNumber: setRoutingNumber,
      checkingAccountNumber: setCheckingAccountNumber,
      confirmAccountNumber: setConfirmAccountNumber,
    };

    if (key && setterMap[key]) {
      setterMap[key]({ value, error: '' });
    }
  };

  const handleShowLoadingScreen = (): void => {
    setShowLoadingScreen(true);
  };

  const handleHideLoadingScreen = (): void => {
    setShowLoadingScreen(false);
  };

  const toggleConfirmationModal = (): void => {
    setShowConfirmationModal(!showConfirmationModal);
  };

  const changePaymentType = (e: React.MouseEvent) => {
    e.preventDefault();
    const trackingData = { currentProperty, user };
    IreneOnboardingCSEvents.emitLinkToAccountManuallyFromAccountInfoPageEvent(trackingData);
    IreneOnboardingCSEvents.emitContinueManuallyFromAccountInfoPageEvent({ ...trackingData });
    setPaymentType('bankAccount');
  };

  const trackOnClickPlaidButton = (): void => {
    IreneOnboardingCSEvents.emitLinkThroughPlaidFromAccountInfoPageEvent({
      currentProperty,
      user,
    });
  };

  const gotoPreviousPage = (): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: 'account_info_modal',
    });
    onBack({
      paymentType,
      routingNumber: routingNumber.value,
      checkingAccountNumber: checkingAccountNumber.value,
    });
  };

  const handleClose = (): void => {
    if (!fromDashboard) {
      IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
        currentProperty,
        user,
        triggerObject: 'account_info_modal',
      });
    }

    onClose();
  };

  const handleCancel = (): void => {
    onCancel();
  };

  const onPlaidSuccess = async (token: any) => {
    handleShowLoadingScreen();
    pkgRef.current = {
      accountInfo: {
        paymentType: 'plaid',
        plaidBankName: token.bankName,
        routingNumber: '',
        checkingAccountNumber: '',
      },
      token: {
        plaidToken: token,
      },
    };

    if (onBoarded) {
      toggleConfirmationModal();
      handleHideLoadingScreen();
      return null;
    }

    await updateDepositMethodAndNavigate();
  };

  const onPlaidExit = (err: any, metadata: any): void => {
    trackValidationError({
      plaidErrorsLastEncountered: err,
      plaidMetadata: metadata,
    });
  };

  const trackOnSubmit = (): void => {
    IreneOnboardingCSEvents.emitContinueFromAccountInfoPageEvent({
      currentProperty,
      user,
    });
  };

  const createStripeBankAccountToken = (): Promise<TokenResponse> => {
    return new Promise((resolve) => {
      stripe
        .createToken('bank_account', {
          country: 'us',
          currency: 'usd',
          routing_number: routingNumber.value,
          account_number: checkingAccountNumber.value,
          account_holder_type: 'individual',
        })
        .then(({ error, token }) => {
          if (error) {
            resolve({ error: error.type, token: '' });
          } else {
            resolve({ error: '', token: token.id });
          }
        });
    });
  };

  const createBankAccountToken = (): Promise<TokenResponse> => {
    trackOnSubmit();
    if (stripe) {
      return createStripeBankAccountToken();
    } else {
      return new Promise((resolve) => resolve({ error: 'No payment provider set', token: '' }));
    }
  };

  const handleSubmitBankAccount = (): Promise<any> | boolean => {
    // Create mutable copies for validation
    const stateForValidation = {
      routingNumber: { value: routingNumber.value, error: routingNumber.error },
      checkingAccountNumber: { value: checkingAccountNumber.value, error: checkingAccountNumber.error },
      confirmAccountNumber: { value: confirmAccountNumber.value, error: confirmAccountNumber.error },
    };

    const validInputs = validateBankInfo({
      state: stateForValidation,
      setState: (newState: any) => {
        setRoutingNumber(newState.routingNumber);
        setCheckingAccountNumber(newState.checkingAccountNumber);
        setConfirmAccountNumber(newState.confirmAccountNumber);
      },
    });
    if (!validInputs) {
      return false;
    }
    return Promise.resolve()
      .then(() => handleShowLoadingScreen())
      .then(createBankAccountToken)
      .then(({ error, token }) => {
        if (error) {
          logError({ error });
          showModalNotification(INVALID_ACCOUNT_INFO);
          return { error };
        }

        pkgRef.current = {
          accountInfo: {
            paymentType,
            routingNumber: routingNumber.value.trim(),
            checkingAccountNumber: checkingAccountNumber.value.trim(),
          },
          token: {
            stripeExternalAccountToken: token,
          },
        };

        if (onBoarded) {
          toggleConfirmationModal();
          handleHideLoadingScreen();
          return null;
        }
        updateDepositMethodAndNavigate();
      });
  };

  const updateDepositMethodAndNavigate = async () => {
    handleShowLoadingScreen();
    try {
      await dispatch(closeNotification());
      await dispatch(saveAccountInfo({ propertyId, accountInfo: pkgRef.current.accountInfo }));
      await updateLandlordPaymentMethod({
        postData: {
          propertyId,
          ...(pkgRef.current.token.stripeExternalAccountToken && {
            stripeExternalAccountToken: pkgRef.current.token.stripeExternalAccountToken,
          }),
          ...(pkgRef.current.token.plaidToken?.publicToken && {
            plaidToken: {
              publicToken: pkgRef.current.token.plaidToken.publicToken,
              accountId: pkgRef.current.token.plaidToken.accountId || '',
            },
          }),
        },
      });
      onUpdatePaymentMethodSuccess();
    } catch (err) {
      logError({ error: err });
      showModalNotification(DEFAULT_ERROR);
    } finally {
      handleHideLoadingScreen();
    }
  };

  const renderPlaidComponent = (): JSX.Element => (
    <Box
      css={{
        display: 'grid',
        gridTemplateRows: 'auto',
        gap: 'token(spacing.sm) !important',
        padding: 'token(spacing.sm) !important',
      }}
    >
      <Anchor css={{ textDecoration: 'none', textAlign: 'center', fontSize: '18px' }} onClick={changePaymentType}>
        Manually enter account information
      </Anchor>
      {getFaqComponent()}
    </Box>
  );

  const renderBankAccountComponent = (): JSX.Element => (
    <>
      <Paragraph>Enter your checking account information.</Paragraph>
      <FormField
        control={
          <Input
            fluid
            size="md"
            onChange={onInputChange}
            value={routingNumber.value}
            className="fs-exclude"
            data-statekey="routingNumber"
            placeholder="111000000"
            error={Boolean(routingNumber.error)}
          />
        }
        description={routingNumber.error && <FormHelp error>{routingNumber.error}</FormHelp>}
        label={
          <Box>
            <Label>Routing number</Label>
            <Tooltip placement="right-end">
              <Image src={CheckImageToolTip as unknown as string} alt="check" />
            </Tooltip>
          </Box>
        }
      />

      <FormField
        control={
          <Input
            fluid
            size="md"
            onChange={onInputChange}
            value={checkingAccountNumber.value}
            className="fs-exclude"
            data-statekey="checkingAccountNumber"
            placeholder="000123456789"
            error={Boolean(checkingAccountNumber.error)}
          />
        }
        description={checkingAccountNumber.error && <FormHelp error>{checkingAccountNumber.error}</FormHelp>}
        label={
          <Box>
            <Label>Account number</Label>
            <Tooltip placement="right-end">
              <Image src={CheckImageToolTip as unknown as string} alt="check" />
            </Tooltip>
          </Box>
        }
      />

      <FormField
        control={
          <Input
            fluid
            size="md"
            onChange={onInputChange}
            value={confirmAccountNumber.value}
            className="fs-exclude"
            data-statekey="confirmAccountNumber"
            error={Boolean(confirmAccountNumber.error)}
          />
        }
        description={confirmAccountNumber.error && <FormHelp error>{confirmAccountNumber.error}</FormHelp>}
        label={<Label>Confirm account number</Label>}
      />
      {getFaqComponent()}
    </>
  );

  const getFaqComponent = (): JSX.Element => {
    let faqs = FaqConstants.ONBOARDING_V2.ACCOUNT_INFO_PAGE;
    if (fromDashboard) {
      faqs = faqs.concat(FaqConstants.DASHBOARD_V2.EDIT_ACCOUNT_INFO_PAGE);
    }
    return <FaqComponent faqs={faqs} />;
  };

  const renderMainContent = (): JSX.Element => (
    <>
      {(selectedTaxVerificationType === 'single_member_llc' || selectedTaxVerificationType === 'multi_member_llc') && (
        <Card outlined tone="soft" css={{ boxShadow: 'none', padding: 'token(spacing.sm) !important' }}>
          <Paragraph>
            <ToneIcon tone="info" css={{ marginRight: 'token(spacing.xs)' }} />
            {selectedTaxVerificationType === 'single_member_llc' ? (
              <>
                The name of the bank account&apos;s holder must match the owner&apos;s name, business name, or DBA name
                you provided.
              </>
            ) : (
              <>The name of the bank account&apos;s holder must match the business name or DBA name you provided.</>
            )}
          </Paragraph>
        </Card>
      )}

      <Paragraph textStyle="body-lg-bold">Where would you like rent to be deposited?</Paragraph>

      <PlaidButton
        plaidEnv={config.plaidEnv}
        plaidLinkToken={config.plaidLinkToken}
        plaidLinkTokenExpiration={config.plaidLinkTokenExpiration}
        getPlaidLinkToken={getPlaidLinkToken}
        label="Link my bank account"
        IconComponent={<IconLockClosedOutline />}
        onSuccess={onPlaidSuccess}
        onExit={onPlaidExit}
        onClickTracking={trackOnClickPlaidButton}
        dataTest="landlord-account-info-link-bank"
        useConstellationButton
        onClickForStorybook={onClickForStorybook}
      />
      <Paragraph css={{ textAlign: 'center' }} textStyle="body-sm">
        Fastest! (You only need your bank login info.)
      </Paragraph>
      <Divider />
      {paymentType === 'plaid' ? renderPlaidComponent() : renderBankAccountComponent()}
    </>
  );

  const renderConfirmationModal = (): JSX.Element => (
    <>
      <Paragraph textStyle="body-lg-bold">Are you sure you want to update your deposit account?</Paragraph>

      <Paragraph>
        Your current deposit account will no longer be stored, and all future payments for all of your properties will
        be deposited into this new account.
      </Paragraph>
    </>
  );

  const renderFooter = (): JSX.Element => {
    if (showConfirmationModal) {
      return (
        <ButtonGroup
          css={{
            padding: 'token(spacing.sm) !important',
          }}
          aria-label="Footer"
        >
          <DebouncedButton emphasis="filled" onClick={updateDepositMethodAndNavigate} loading={showLoadingScreen}>
            Update my account
          </DebouncedButton>
          <Button emphasis="outlined" onClick={toggleConfirmationModal}>
            Cancel
          </Button>
        </ButtonGroup>
      );
    } else {
      return (
        <ButtonGroup
          css={{
            padding: 'token(spacing.sm) !important',
          }}
          aria-label="Footer"
        >
          {paymentType !== 'plaid' && (
            <DebouncedButton
              onClick={handleSubmitBankAccount}
              icon={<IconLockClosedOutline />}
              dataTest="landlord-account-info-link-checking-account"
              loading={showLoadingScreen}
            >
              {accountInfo.editMode ? 'Update my account' : 'Link to this account'}
            </DebouncedButton>
          )}
          {accountInfo.editMode && (
            <Button data-statekey="Cancel" onClick={handleCancel} emphasis="outlined">
              Cancel
            </Button>
          )}
        </ButtonGroup>
      );
    }
  };

  const renderModal = () => {
    if (showConfirmationModal) {
      return renderConfirmationModal();
    } else {
      return renderMainContent();
    }
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      onRequestClose={handleClose}
      header={
        <Box css={{ padding: 'token(spacing.sm) !important' }}>
          {!fromDashboard && (
            <TextButton textStyle="body-sm-bold" tone="brand" css={{ float: 'left' }} onClick={gotoPreviousPage}>
              Back
            </TextButton>
          )}
          <Heading level={3} textStyle="heading-xs" css={{ display: 'flex', justifyContent: 'center' }}>
            Payment setup
          </Heading>
        </Box>
      }
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'token(spacing.sm) !important',
            padding: 'token(spacing.lg) !important',
          }}
        >
          {renderModal()}
        </Box>
      }
      footer={renderFooter()}
    />
  );
};

export default AccountInfoPage;
